// ============================================================
// Braval - Auction View Page
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { Header } from '../App';

function formatDate(value) {
    if (!value) return null;
    return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatCurrency(value) {
    if (value == null) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}

// ------------------------------------------------------------
// Auction Header
// ------------------------------------------------------------
function AuctionHeader({ project, lots }) {
    const navigate = useNavigate();

    const totalValue = lots.reduce((sum, l) => sum + (l.sale_price != null ? parseFloat(l.sale_price) : 0), 0);
    const hasSales   = lots.some(l => l.sale_price != null);
    const bp         = project.buyers_premium != null ? parseFloat(project.buyers_premium) : null;
    const tax        = project.sales_tax      != null ? parseFloat(project.sales_tax)      : null;

    const meta = [];
    if (hasSales)                       meta.push({ label: 'Total Value', value: formatCurrency(totalValue) });
    meta.push({ label: 'Lot Count', value: lots.length });
    if (project.auction_date)           meta.push({ label: 'Date',        value: formatDate(project.auction_date) });
    if (project.location_region)    meta.push({ label: 'Location',         value: project.location_region });
    if (project.auctioneer_firm)    meta.push({ label: 'Auctioneer',       value: project.auctioneer_firm });
    if (project.data_source)        meta.push({ label: 'Data Source',      value: project.data_source });
    if (bp  != null)                meta.push({ label: "Buyer's Premium",   value: `${bp.toFixed(1)}%` });
    if (tax != null)                meta.push({ label: 'Sales Tax',         value: `${tax.toFixed(1)}%` });
    if (hasSales && bp  != null)    meta.push({ label: 'Est. BP Paid',      value: formatCurrency(totalValue * bp  / 100) });
    if (hasSales && tax != null)    meta.push({ label: 'Est. Tax Collected', value: formatCurrency(totalValue * tax / 100) });
    if (project.reporting_user)     meta.push({ label: 'Reported By',       value: project.reporting_user });

    return (
        <div className="auction-view-header">
            <div className="auction-view-name-row">
                <h2 className="auction-view-name">{project.project_name}</h2>
                <div className="auction-view-actions">
                    <button onClick={() => navigate('/auctions')} className="search-button auction-back-button">← Back</button>
                    <Link to={`/auctions/${project.project_id}/edit`} className="search-button auction-edit-button">Edit</Link>
                </div>
            </div>
            <div className="auction-view-meta">
                {meta.map(item => (
                    <div key={item.label} className="auction-view-meta-item">
                        <span className="auction-view-meta-label">{item.label}</span>
                        <span className="auction-view-meta-value">{item.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Natural sort for lot numbers: "2" < "10" < "10A" < "11"
function compareLotNumbers(a, b) {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

const LOT_COLUMNS = [
    { key: 'lot_number',    label: 'Lot #',      className: 'col-lot',   defaultDir: 'asc'  },
    { key: 'description',   label: 'Description', className: 'col-description', defaultDir: 'asc' },
    { key: 'number_of_bids',label: 'Bids',       className: 'col-price', defaultDir: 'desc' },
    { key: 'sale_price',    label: 'Sale Price', className: 'col-price', defaultDir: 'desc' },
];

function sortLots(lots, key, dir) {
    return [...lots].sort((a, b) => {
        let av = a[key], bv = b[key];
        if (key === 'lot_number') {
            const cmp = compareLotNumbers(av, bv);
            return dir === 'asc' ? cmp : -cmp;
        }
        if (key === 'description') {
            av = (a.title || a.description || '').toLowerCase();
            bv = (b.title || b.description || '').toLowerCase();
        }
        if (av == null) return 1;
        if (bv == null) return -1;
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return dir === 'asc' ? cmp : -cmp;
    });
}

// ------------------------------------------------------------
// Lots Table
// ------------------------------------------------------------
function LotsTable({ lots }) {
    const [sortKey, setSortKey] = useState('lot_number');
    const [sortDir, setSortDir] = useState('asc');

    const handleSort = useCallback((col) => {
        setSortKey(prev => {
            if (prev === col.key) {
                setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                return prev;
            }
            setSortDir(col.defaultDir);
            return col.key;
        });
    }, []);

    if (lots.length === 0) {
        return <div className="results-status">No lots found for this auction.</div>;
    }

    const sorted = sortLots(lots, sortKey, sortDir);

    return (
        <>
            <table className="results-table">
                <thead>
                    <tr>
                        {LOT_COLUMNS.map(col => (
                            <th
                                key={col.key}
                                className={`sortable-th ${col.className}`}
                                onClick={() => handleSort(col)}
                            >
                                {col.label}
                                <span className="sort-indicator">
                                    {sortKey === col.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                </span>
                            </th>
                        ))}
                        <th className="col-actions"></th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((lot) => (
                        <tr key={lot.lot_id}>
                            <td className="col-lot">{lot.lot_number || '—'}</td>
                            <td className="col-description">
                                <LotDescriptionCell lot={lot} />
                            </td>
                            <td className="col-price">{lot.number_of_bids ?? '—'}</td>
                            <td className="col-price sale-price">{formatCurrency(lot.sale_price)}</td>
                            <td className="col-actions">
                                <Link to={`/lots/${lot.lot_id}/edit`} className="search-button row-edit-button">Edit</Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

function LotDescriptionCell({ lot }) {
    const hasTags = lot.make || lot.model || lot.year || lot.serial_number;
    return (
        <div className="description-cell">
            {lot.title && <div className="description-title">{lot.title}</div>}
            {lot.description && <div className="description-text">{lot.description}</div>}
            {hasTags && (
                <div className="description-tags">
                    {lot.make && (
                        <span className="description-tag">
                            <span className="tag-label">Make</span><strong>{lot.make}</strong>
                        </span>
                    )}
                    {lot.model && (
                        <span className="description-tag">
                            <span className="tag-label">Model</span><strong>{lot.model}</strong>
                        </span>
                    )}
                    {lot.year && (
                        <span className="description-tag">
                            <span className="tag-label">Year</span><strong>{lot.year}</strong>
                        </span>
                    )}
                    {lot.serial_number && (
                        <span className="description-tag">
                            <span className="tag-label">S/N</span><strong>{lot.serial_number}</strong>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

// ------------------------------------------------------------
// Page
// ------------------------------------------------------------
export default function AuctionViewPage() {
    const { id } = useParams();
    const [project, setProject] = useState(null);
    const [lots,    setLots]    = useState([]);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState(null);

    useEffect(() => {
        const fetchAuction = async () => {
            try {
                const res  = await apiFetch(`/api/auctions/${id}`);
                const data = await res.json();
                if (!res.ok) {
                    setError(data.error || 'Failed to load auction.');
                    return;
                }
                setProject(data.project);
                setLots(data.lots);
            } catch {
                setError('Could not reach the API.');
            } finally {
                setLoading(false);
            }
        };
        fetchAuction();
    }, [id]);

    return (
        <div className="app-layout">
            <Header />
            <main className="auction-view-page">
                {loading && <div className="results-status">Loading...</div>}
                {error   && <div className="results-status results-error">{error}</div>}
                {!loading && !error && project && (
                    <>
                        <AuctionHeader project={project} lots={lots} />
                        <LotsTable lots={lots} />
                    </>
                )}
            </main>
        </div>
    );
}
